/**
 * Every per-tier cost cap in one place: AI calls, speech-to-text, Piper
 * renders and the monthly voice-credit budget. There is no free tier: NONE is
 * all zeros, and anything not entitled gets the paywall.
 *
 * Paid voices share one budget of voice credits (1 credit = $0.001 of 2027
 * provider cost, see voiceAllowance.ts VOICE_CREDIT_RATES); every listen burns
 * credits at its voice's rate, cached or not.
 *
 * Worst case (every cap maxed, nothing cached, 2027 prices, yearly via an app
 * store at 15%), at the proposed $79.99 / $169.99 yearly prices: Pro $2.83 of
 * cost on $5.67 net (50%), Elite $5.46 on $12.04 net (55%). Unit costs: $0.0013
 * per AI question, $0.006/min STT (whisper-1), Piper $0.017 per uncached hour
 * (~120 chunks), hosting $0.25. See docs/jarvis-profitability.md.
 *
 * Tune without a deploy via PLAN_CAPS_JSON, e.g. {"PRO":{"voiceCredits":2000}}
 * (partial per-tier overrides, merged over these defaults).
 */
export type CapTier = 'NONE' | 'PRO' | 'ELITE' | 'COMP';

export interface TierCaps {
  aiCallsPerMonth: number;
  aiCallsPerDay: number;
  sttMinutesPerMonth: number;
  /** Never-rendered Piper chunks (~120 per audio hour). Cached chunks are free. */
  piperChunksPerHour: number;
  piperChunksPerDay: number;
  /** Monthly voice credits shared by WaveNet, Gemini performance and ElevenLabs (1 credit = $0.001). */
  voiceCredits: number;
}

const ZERO: TierCaps = {
  aiCallsPerMonth: 0, aiCallsPerDay: 0, sttMinutesPerMonth: 0, piperChunksPerHour: 0,
  piperChunksPerDay: 0, voiceCredits: 0,
};

const PRO: TierCaps = {
  aiCallsPerMonth: 300,
  aiCallsPerDay: 40,
  sttMinutesPerMonth: 30,
  piperChunksPerHour: 150,
  piperChunksPerDay: 60,
  // ≈ 8.1 h WaveNet, or 1.5 h performance, or 22k ElevenLabs v3 characters.
  voiceCredits: 1_750,
};

const ELITE: TierCaps = {
  aiCallsPerMonth: 750,
  aiCallsPerDay: 150,
  sttMinutesPerMonth: 120,
  piperChunksPerHour: 150,
  piperChunksPerDay: 120,
  // ≈ 13.9 h WaveNet, or 2.6 h performance, or 37.5k ElevenLabs v3 characters.
  voiceCredits: 3_000,
};

/** Internal comp accounts (store reviewers): Pro's limits on the free voice; paid voices stay off. */
const COMP: TierCaps = { ...PRO, voiceCredits: 0 };

export const DEFAULT_PLAN_CAPS: Record<CapTier, TierCaps> = { NONE: ZERO, PRO, ELITE, COMP };

export function mergeCaps(base: Record<CapTier, TierCaps>, json: string | undefined): Record<CapTier, TierCaps> {
  if (!json) return base;
  try {
    const over = JSON.parse(json) as Partial<Record<CapTier, Partial<TierCaps>>>;
    const out = { ...base };
    for (const tier of ['PRO', 'ELITE', 'COMP'] as const) {
      const o = over?.[tier];
      if (!o || typeof o !== 'object') continue;
      const merged = { ...out[tier] };
      for (const k of Object.keys(merged) as (keyof TierCaps)[]) {
        const v = Number((o as any)[k]);
        if ((o as any)[k] !== undefined && Number.isFinite(v) && v >= 0) merged[k] = v;
      }
      out[tier] = merged;
    }
    return out;
  } catch {
    return base;
  }
}

export const PLAN_CAPS: Record<CapTier, TierCaps> = mergeCaps(DEFAULT_PLAN_CAPS, typeof process !== 'undefined' ? process.env?.PLAN_CAPS_JSON : undefined);

export function capsFor(tier: CapTier): TierCaps {
  return PLAN_CAPS[tier] ?? ZERO;
}
